import {Input, NumberInput} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Collapse} from 'src/design/atoms/Collapse';
import {Grid} from 'src/design/atoms/Grid';
import {Switch} from 'src/design/atoms/Switch';
import React from 'react';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import {SectionCard} from 'src/design/atoms/SectionCard';
import type {InstanceFormValues, InstanceSectionProps} from './SectionProps';
import {ViewField, ViewSwitch} from 'src/design/atoms/ViewField';

export const BadgeExpirationSection: React.FC<InstanceSectionProps> = ({
  mode,
}) => {
  const {control} = useFormContext<InstanceFormValues>();
  const formData = useWatch({control});
  const isViewMode = mode === 'view';
  const isEnabled = !!formData.isBadgeExpirationEnabled;

  if (isViewMode) {
    return (
      <SectionCard
        title="Badge Expiration"
        data-testid="section-badge-expiration">
        <Box p="md">
          <Grid gutter="md">
            <Grid.Col span={{base: 12, md: 4}}>
              <ViewSwitch label="Badge Expiration" value={isEnabled} />
            </Grid.Col>
            {isEnabled && (
              <>
                <Grid.Col span={{base: 12, md: 4}}>
                  <ViewField
                    label="Default Expiration Days"
                    value={formData.defaultBadgeExpirationDays}
                  />
                </Grid.Col>
                <Grid.Col span={{base: 12, md: 4}}>
                  <ViewField
                    label="Deactivation Note"
                    value={formData.badgeExpirationDeactivationNote}
                  />
                </Grid.Col>
              </>
            )}
          </Grid>
        </Box>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Badge Expiration"
      data-testid="section-badge-expiration">
      <Box p="md">
        <Controller
          name="isBadgeExpirationEnabled"
          control={control}
          render={({field}) => (
            <Switch
              data-testid="form-field-isBadgeExpirationEnabled"
              label="Enable Badge Expiration"
              description="Contractors with an expired badge will be automatically deactivated by a daily scheduled job."
              color="green.8"
              checked={!!field.value}
              onChange={e => field.onChange(e.currentTarget.checked)}
              mb="md"
            />
          )}
        />

        <Collapse in={isEnabled}>
          <Box
            pl="md"
            style={{borderLeft: '2px solid var(--mantine-color-blue-2)'}}>
            <Grid gutter="md">
              <Grid.Col span={{base: 12, md: 6}}>
                <Controller
                  name="defaultBadgeExpirationDays"
                  control={control}
                  render={({field}) => (
                    <NumberInput
                      data-testid="form-field-defaultBadgeExpirationDays"
                      label="Default Expiration Days"
                      description="Days from signup until badge expires. Leave empty for manual entry."
                      value={field.value ?? ''}
                      onChange={val =>
                        field.onChange(
                          typeof val === 'number' ? val : undefined
                        )
                      }
                      onBlur={field.onBlur}
                      min={1}
                      placeholder="Enter number of days"
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={{base: 12, md: 6}}>
                <Controller
                  name="badgeExpirationDeactivationNote"
                  control={control}
                  render={({field}) => (
                    <Input.Wrapper
                      data-testid="form-field-badgeExpirationDeactivationNote"
                      label="Deactivation Note"
                      description="Custom note added to a contractor's profile when deactivated.">
                      <Input
                        data-testid="form-field-badgeExpirationDeactivationNote-input"
                        value={field.value || ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        placeholder="e.g., Badge expired — please contact your supervisor"
                        autoComplete="new-password"
                      />
                    </Input.Wrapper>
                  )}
                />
              </Grid.Col>
            </Grid>
          </Box>
        </Collapse>
      </Box>
    </SectionCard>
  );
};
